import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import queryString from 'query-string'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'
import {
    SLATemplate,
    SLATemplatePayload,
    ServiceSLA,
    ServiceSLAPayload,
    ApplyTemplatePayload,
    SLADowntimeLog,
    DowntimeLogPayload,
    EndDowntimePayload,
    UptimeStats,
    MonthlySummary,
    SLATier,
    TicketSLASummary,
    TicketAtRisk,
    TicketApproaching,
    ComplianceOverview,
    CustomerCompliance,
    ServiceAtRisk,
    SLAChangeLog,
    SLAHistoryEntry,
} from '../types/sla'

// ============== SLA Templates ==============

export const getSLATemplates = async (payload?: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<SLATemplate>>(
        `/sla-templates?${queryString.stringify(payload || {})}`
    )
    return response?.data
}

export const getActiveSLATemplates = async () => {
    const response = await datacentral.get<ResponseArray<SLATemplate>>(
        '/sla-templates/active'
    )
    return response?.data?.results || []
}

export const getSLATemplatesByTier = async (tier: SLATier) => {
    const response = await datacentral.get<ResponseArray<SLATemplate>>(
        `/sla-templates/tier/${tier}`
    )
    return response?.data
}

export const getSLATemplate = async (templateId: number) => {
    const response = await datacentral.get<ResponseObject<SLATemplate>>(
        `/sla-templates/${templateId}`
    )
    return response?.data
}

export const createSLATemplate = async (payload: SLATemplatePayload) => {
    const response = await datacentral.post<ResponseObject<SLATemplate>>(
        '/sla-templates',
        payload
    )
    return response?.data
}

export const updateSLATemplate = async (
    templateId: number,
    payload: SLATemplatePayload
) => {
    const response = await datacentral.put<ResponseObject<SLATemplate>>(
        `/sla-templates/${templateId}`,
        payload
    )
    return response?.data
}

export const deleteSLATemplate = async (templateId: number) => {
    const response = await datacentral.delete(`/sla-templates/${templateId}`)
    return response?.data
}

// ============== Service SLA ==============

export const getServiceSLAs = async (payload?: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<ServiceSLA>>(
        `/service-slas?${queryString.stringify(payload || {})}`
    )
    return response?.data
}

export const getServiceSLA = async (serviceId: number) => {
    const response = await datacentral.get<ResponseObject<ServiceSLA>>(
        `/services/${serviceId}/sla`
    )
    return response?.data
}

export const createServiceSLA = async (
    serviceId: number,
    payload: ServiceSLAPayload
) => {
    const response = await datacentral.post<ResponseObject<ServiceSLA>>(
        `/services/${serviceId}/sla`,
        payload
    )
    return response?.data
}

export const updateServiceSLA = async (
    serviceId: number,
    payload: ServiceSLAPayload
) => {
    const response = await datacentral.put<ResponseObject<ServiceSLA>>(
        `/services/${serviceId}/sla`,
        payload
    )
    return response?.data
}

export const deleteServiceSLA = async (serviceId: number) => {
    const response = await datacentral.delete(`/services/${serviceId}/sla`)
    return response?.data
}

export const applyTemplateToService = async (
    serviceId: number,
    payload: ApplyTemplatePayload
) => {
    const response = await datacentral.post<ResponseObject<ServiceSLA>>(
        `/services/${serviceId}/sla/apply-template`,
        payload
    )
    return response?.data
}

// ============== Downtime Tracking ==============

export const getOngoingDowntimes = async () => {
    const response = await datacentral.get<ResponseArray<SLADowntimeLog>>(
        '/sla/downtimes/ongoing'
    )
    return response?.data
}

export const getServiceDowntimeLogs = async (
    serviceId: number,
    payload?: DataGridPayload
) => {
    const response = await datacentral.get<ResponseArray<SLADowntimeLog>>(
        `/services/${serviceId}/sla/downtime?${queryString.stringify(
            payload || {}
        )}`
    )
    return response?.data
}

export const getServiceDowntimeByPeriod = async (
    serviceId: number,
    startDate: string,
    endDate: string
) => {
    const response = await datacentral.get<ResponseArray<SLADowntimeLog>>(
        `/services/${serviceId}/sla/downtime/period?start_date=${startDate}&end_date=${endDate}`
    )
    return response?.data
}

export const getServiceUptimeStats = async (
    serviceId: number,
    startDate: string,
    endDate: string
) => {
    const response = await datacentral.get<ResponseObject<UptimeStats>>(
        `/services/${serviceId}/sla/downtime/uptime?start_date=${startDate}&end_date=${endDate}`
    )
    return response?.data
}

export const getServiceMonthlySummary = async (
    serviceId: number,
    year: number,
    month: number
) => {
    const response = await datacentral.get<ResponseArray<MonthlySummary>>(
        `/services/${serviceId}/sla/downtime/monthly-summary?year=${year}&month=${month}`
    )
    return response?.data
}

export const createDowntimeLog = async (
    serviceId: number,
    payload: DowntimeLogPayload
) => {
    const response = await datacentral.post<ResponseObject<SLADowntimeLog>>(
        `/services/${serviceId}/sla/downtime`,
        payload
    )
    return response?.data
}

export const updateDowntimeLog = async (
    serviceId: number,
    downtimeId: number,
    payload: DowntimeLogPayload
) => {
    const response = await datacentral.put<ResponseObject<SLADowntimeLog>>(
        `/services/${serviceId}/sla/downtime/${downtimeId}`,
        payload
    )
    return response?.data
}

export const endDowntime = async (
    serviceId: number,
    downtimeId: number,
    payload: EndDowntimePayload
) => {
    const response = await datacentral.put<ResponseObject<SLADowntimeLog>>(
        `/services/${serviceId}/sla/downtime/${downtimeId}/end`,
        payload
    )
    return response?.data
}

export const deleteDowntimeLog = async (
    serviceId: number,
    downtimeId: number
) => {
    const response = await datacentral.delete(
        `/services/${serviceId}/sla/downtime/${downtimeId}`
    )
    return response?.data
}

// ============== SLA Dashboard ==============

export interface DashboardOverview {
    total_services_with_sla: number
    period_minutes: number
    total_sla_downtime_minutes: number
    total_all_downtime_minutes: number
    total_incidents: number
    services_meeting_sla: number
    services_breaching_sla: number
    avg_uptime_percent: number
    total_provider_downtime: number
    total_customer_downtime: number
    total_third_party_downtime: number
    total_scheduled_downtime: number
}

export interface ServiceUptimeItem {
    service_id: number
    service_name: string
    customer_id: number
    customer_name: string
    target_uptime: number
    allowed_downtime: number
    sla_downtime_minutes: number
    incident_count: number
    period_minutes: number
    actual_uptime: number
    sla_met: boolean
}

export interface CustomerUptimeStats {
    customer_id: number
    total_services: number
    period_minutes: number
    total_sla_downtime_minutes: number
    total_all_downtime_minutes: number
    total_incidents: number
    services_meeting_sla: number
    services_breaching_sla: number
    avg_uptime_percent: number
}

export interface DashboardQueryParams {
    start_date?: string
    end_date?: string
    limit?: number
    offset?: number
}

/**
 * Get SLA dashboard overview - aggregate stats for ALL active SLAs
 */
export const getSLADashboardOverview = async (
    params?: DashboardQueryParams
) => {
    const response = await datacentral.get<ResponseObject<DashboardOverview>>(
        `/sla/dashboard/overview?${queryString.stringify(params || {})}`
    )
    return response?.data
}

/**
 * Get paginated list of all services with uptime stats
 */
export const getSLADashboardServices = async (
    params?: DashboardQueryParams
) => {
    const response = await datacentral.get<ResponseArray<ServiceUptimeItem>>(
        `/sla/dashboard/services?${queryString.stringify(params || {})}`
    )
    return response?.data
}

/**
 * Get aggregate uptime stats for a customer's services
 */
export const getCustomerUptimeStats = async (
    customerId: number,
    params?: DashboardQueryParams
) => {
    const response = await datacentral.get<ResponseObject<CustomerUptimeStats>>(
        `/sla/dashboard/customers/${customerId}?${queryString.stringify(
            params || {}
        )}`
    )
    return response?.data
}

/**
 * Get list of customer's services with individual uptime stats
 */
export const getCustomerServicesUptime = async (
    customerId: number,
    params?: DashboardQueryParams
) => {
    const response = await datacentral.get<ResponseArray<ServiceUptimeItem>>(
        `/sla/dashboard/customers/${customerId}/services?${queryString.stringify(
            params || {}
        )}`
    )
    return response?.data
}

// ============== Service SLA - Additional APIs ==============

/**
 * Get SLAs for customer's services
 */
export const getCustomerServiceSLAs = async (
    customerId: number,
    params?: DataGridPayload
) => {
    const response = await datacentral.get<ResponseArray<ServiceSLA>>(
        `/service-slas/customers/${customerId}?${queryString.stringify(params || {})}`
    )
    return response?.data
}

/**
 * Get recent SLA changes (admin dashboard)
 */
export const getSLAChanges = async (params?: DataGridPayload) => {
    const response = await datacentral.get<ResponseArray<SLAChangeLog>>(
        `/service-slas/changes?${queryString.stringify(params || {})}`
    )
    return response?.data
}

/**
 * Get SLA change history for a service
 */
export const getServiceSLAHistory = async (
    serviceId: number,
    params?: DataGridPayload
) => {
    const response = await datacentral.get<ResponseArray<SLAHistoryEntry>>(
        `/services/${serviceId}/sla/history?${queryString.stringify(params || {})}`
    )
    return response?.data
}

// ============== Ticket SLA Compliance ==============

export interface TicketComplianceQueryParams {
    limit?: number
    offset?: number
}

/**
 * Get summary of at-risk and approaching tickets
 */
export const getTicketSLASummary = async () => {
    const response = await datacentral.get<ResponseObject<TicketSLASummary>>(
        '/sla/tickets/summary'
    )
    return response?.data
}

/**
 * Get tickets that breached SLA deadline (past 100%)
 */
export const getTicketsAtRisk = async (
    params?: TicketComplianceQueryParams
) => {
    const response = await datacentral.get<ResponseArray<TicketAtRisk>>(
        `/sla/tickets/at-risk?${queryString.stringify(params || {})}`
    )
    return response?.data
}

/**
 * Get tickets approaching deadline (80-100% elapsed)
 */
export const getTicketsApproaching = async (
    params?: TicketComplianceQueryParams
) => {
    const response = await datacentral.get<ResponseArray<TicketApproaching>>(
        `/sla/tickets/approaching?${queryString.stringify(params || {})}`
    )
    return response?.data
}

// ============== Compliance Dashboard ==============

export interface ComplianceQueryParams {
    start_date?: string
    end_date?: string
    period_type?: 'daily' | 'weekly' | 'monthly'
    limit?: number
    offset?: number
}

/**
 * Get aggregate compliance metrics (response/resolution time)
 */
export const getComplianceOverview = async (params?: ComplianceQueryParams) => {
    const response = await datacentral.get<ResponseObject<ComplianceOverview>>(
        `/sla/compliance/overview?${queryString.stringify(params || {})}`
    )
    return response?.data
}

/**
 * Get compliance aggregated by customer
 */
export const getComplianceByCustomers = async (
    params?: ComplianceQueryParams
) => {
    const response = await datacentral.get<ResponseArray<CustomerCompliance>>(
        `/sla/compliance/customers?${queryString.stringify(params || {})}`
    )
    return response?.data
}

/**
 * Get services with at-risk tickets today
 */
export const getComplianceAtRiskServices = async (
    params?: TicketComplianceQueryParams
) => {
    const response = await datacentral.get<ResponseArray<ServiceAtRisk>>(
        `/sla/compliance/at-risk?${queryString.stringify(params || {})}`
    )
    return response?.data
}
